<script setup lang="ts">
import { computed } from 'vue'
import type { Verdict } from '@/lib/verdict'
import { absoluteTime, duration, relativeTime } from '@/lib/format'

const props = defineProps<{
  verdict: Verdict
  target: string
  scannedAt?: string | null
  finishedAt?: string | null
}>()

// Pending and running are the only states worth interrupting a screen reader
// for, and even then only once: the headline keeps changing while a scan
// polls, and a live region that spoke every change would talk over itself.
// The terminal kinds go quiet so the panel does not re-announce itself on
// every unrelated re-render once the verdict has already settled.
const liveMode = computed<'polite' | 'off'>(() =>
  props.verdict.kind === 'pending' || props.verdict.kind === 'running' ? 'polite' : 'off',
)

interface Meta {
  readonly label: 'Finished' | 'Started'
  readonly iso: string
}

const meta = computed<Meta | null>(() => {
  if (props.finishedAt) return { label: 'Finished', iso: props.finishedAt }
  if (props.scannedAt) return { label: 'Started', iso: props.scannedAt }
  return null
})

const elapsed = computed(() => duration(props.scannedAt, props.finishedAt))
</script>

<template>
  <section
    class="card relative flex overflow-hidden"
    :data-tone="props.verdict.tone"
    role="status"
    :aria-live="liveMode"
  >
    <div
      class="tone-stripe w-1 shrink-0"
      aria-hidden="true"
    />
    <div class="flex flex-1 flex-col gap-3 p-6 sm:p-8">
      <p class="eyebrow">
        Report for
      </p>
      <p class="font-mono text-lg break-all">
        {{ props.target }}
      </p>
      <h1 class="verdict-headline text-3xl sm:text-4xl font-semibold tracking-tight">
        {{ props.verdict.headline }}
      </h1>
      <p class="max-w-prose text-ink-muted">
        {{ props.verdict.detail }}
      </p>
      <p
        v-if="meta"
        class="text-sm text-ink-faint"
      >
        {{ meta.label }}
        <time :title="absoluteTime(meta.iso)">{{ relativeTime(meta.iso) }}</time>
        <span v-if="elapsed"> · took {{ elapsed }}</span>
      </p>
    </div>
  </section>
</template>

<style scoped>
/* `--tone-ink` rather than `--tone-fg`: this card never takes the tone
   background, and the critical tone's `--tone-fg` is white for use on the solid
   fill it has elsewhere. The special case lives in main.css now, so there is
   nothing per-tone to handle here. */
.verdict-headline {
  color: var(--tone-ink);
}
</style>
